import React, { useEffect, useRef, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  Timeline,
  Product,
  DailyPlanEntry,
  TimelineBatch,
  TimelineModification,
  Machine,
  ProductMachineConfig,
  ProductionDayResult,
  ProductionPhase,
  BatchStatus,
  MES_SETTING_KEYS,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import OutlinedButton from "../../../components/Buttons/OutlinedButton";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";
import LotAssignment from "./LotAssignment";
import ProductSpecCard from "../../../components/MES/ProductSpecCard";
import GanttPlannerChart from "../../../components/MES/GanttPlannerChart";
import TimelineModificationsPanel from "./TimelineModificationsPanel";
import TimelinePhaseEditor from "./TimelinePhaseEditor";
import OperationalReorderPanel from "./OperationalReorderPanel";
import SemiFinishedScheduleStep from "./SemiFinishedScheduleStep";
import PlanWizardStepper, { WizardStep } from "./PlanWizardStepper";
import {
  applySemiFinishedDateEdits,
  applySemiFinishedDateOverrides,
  assignBatchKeys,
  batchPhaseRefs,
  batchesToDailyPlan,
  clipPhaseToDay,
  collectPhaseDays,
  collectSemiFinishedDateOverrides,
  formatHrDay,
  getBatchKey,
  getUniqueMixers,
  getUniqueProductNames,
  isGanttResourceGrouping,
  batchActiveOnDay,
  reorderBatchInGroup,
  toTimelineModificationPayload,
  type GanttGroupBy,
  type SemiFinishedDateOverrides,
} from "./planningUtils";
import { formatClockTime } from "../../../utils/processParameterSerialize";
import { phaseColor, phaseName } from "./phaseDisplay";

const BATCH_STATUS_OPTIONS: BatchStatus[] = [
  "planned", "ready", "in_progress", "completed", "on_hold", "skipped", "canceled",
];

const WIZARD_STEPS: WizardStep[] = [
  { id: 1, label: "Proizvodi", description: "Definiranje proizvoda" },
  { id: 2, label: "Polugotovi", description: "Raspored po danima" },
  { id: 3, label: "Raspored", description: "Faze po proizvodu" },
  { id: 4, label: "Gantt", description: "Pregled i potvrda" },
];

const emptyPlanRow = (): DailyPlanEntry => ({
  product_id: 0,
  product_name: "",
  batch_count: 1,
  mass_kg: 0,
});

/** Step 1 as it must look for a day that has no plan yet: a single blank row. */
const createEmptyDailyPlan = (): DailyPlanEntry[] => [emptyPlanRow()];

/**
 * Planning Page - Daily Plan & Timeline Management
 * Allows creating daily production plans and generating/confirming timelines
 */
const Planning = () => {
  savePathToLocalStorage();

  const [products, setProducts] = useState<Product[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [machineConfigs, setMachineConfigs] = useState<ProductMachineConfig[]>([]);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [generatedBatches, setGeneratedBatches] = useState<Partial<TimelineBatch>[]>([]);
  const [dailyPlan, setDailyPlan] = useState<DailyPlanEntry[]>(createEmptyDailyPlan);
  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate(true)); // Prefer tomorrow for planning
  const [loading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [lotAssignmentModalOpen, setLotAssignmentModalOpen] = useState(false);
  const [confirmNotes, setConfirmNotes] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [modifications, setModifications] = useState<TimelineModification[]>([]);
  const [timelineView, setTimelineView] = useState<"gantt" | "table">("gantt");
  const [ganttGroupBy, setGanttGroupBy] = useState<GanttGroupBy>("machine");
  const [revisionReason, setRevisionReason] = useState("");
  // Editing a confirmed plan in place (add/remove/change products, reorder).
  // The confirmed timeline in the DB stays intact until the edit is confirmed
  // as a new revision.
  const [editingConfirmed, setEditingConfirmed] = useState(false);
  const [wizardStep, setWizardStep] = useState<number>(1);
  // Plan changed since the last generation → the schedule must be regenerated
  // before advancing to the schedule/gantt steps.
  const [planDirty, setPlanDirty] = useState(false);
  // Everything physically produced on the selected day (incl. semi-finished
  // made today for other days' plans). Read-only, reflects saved state.
  const [productionDay, setProductionDay] = useState<ProductionDayResult | null>(null);
  // Production day chosen for each semi-finished product (wizard step 2).
  // Generating a schedule rebuilds every batch from scratch and re-derives the
  // semi-finished days from the DB schedule rule, so the planner's choice has to
  // be remembered here and re-applied after each regeneration.
  const [semiFinishedDates, setSemiFinishedDates] = useState<SemiFinishedDateOverrides>({});
  // Start of the production day (first mixing) for this plan, "HH:MM". Empty
  // means "follow the global setting" — which is also what is shown as the
  // placeholder, so the planner can see what they'd be overriding.
  const [shiftStart, setShiftStart] = useState<string>("");
  const [globalShiftStart, setGlobalShiftStart] = useState<string>("");

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
    setWizardStep(1);
    setPlanDirty(false);
    setEditingConfirmed(false);
    setSemiFinishedDates({});
    setShiftStart("");
    // The new date's plan is loaded asynchronously, so step 1 has to be cleared
    // here as well — otherwise the previous day's products stay on screen and,
    // for a day without a plan, are never replaced.
    setDailyPlan(createEmptyDailyPlan());
    setModifications([]);
  };

  // Load products on mount
  useEffect(() => {
    loadProducts();
    loadMachines();
    loadMachineConfigs();
    loadShiftStartDefault();
  }, []);

  const loadShiftStartDefault = async () => {
    try {
      const settings = await mesApi.getMesSettings();
      const value = settings.find((s) => s.SettingKey === MES_SETTING_KEYS.SHIFT_START)?.SettingValue;
      if (value) setGlobalShiftStart(value);
    } catch {
      // Falls back to an empty placeholder; the engine still has its default.
    }
  };

  // Also runs on mount, so the timeline is loaded exactly once per date.
  useEffect(() => {
    loadTimelineForDate(selectedDate);
  }, [selectedDate]);

  const loadProducts = async () => {
    try {
      const data = await mesApi.getProducts({ is_active: true });
      setProducts(data);
    } catch (err: any) {
      toast.error("Failed to load products: " + err.message);
    }
  };

  const loadMachines = async () => {
    try {
      setMachines(await mesApi.getMachines());
    } catch (err: any) {
      // Devices remain selectable from product defaults even without this list.
    }
  };

  const loadMachineConfigs = async () => {
    try {
      setMachineConfigs(await mesApi.getProductMachineConfig());
    } catch (err: any) {
      // Worker counts fall back to 1 when no predefined config exists.
    }
  };

  // Active machines for a given role; falls back to all machines when the type
  // metadata doesn't match (machine types are not guaranteed to be populated).
  const machinesForRole = (role: "mixer" | "processor" | "oven"): Machine[] => {
    const active = machines.filter((m) => m.IsActive !== false);
    const byType = active.filter((m) => m.MachineType === role);
    return byType.length ? byType : active;
  };

  const workersFor = (productId: number, machineId?: number): number | undefined => {
    if (!machineId) return undefined;
    const cfg = machineConfigs.find(
      (c) => c.Product === productId && c.Machine === machineId
    );
    return cfg?.Workers;
  };

  // Predefined devices + per-phase workers for a product (DB defaults).
  // NOTE: these legacy product columns (MixerMachine, ...) are increasingly
  // null because devices/workers are configured via ProductionPhase rows. The
  // phase-sourced values are merged in asynchronously by applyPhaseDefaults.
  const productPlanDefaults = (product?: Product): Partial<DailyPlanEntry> => {
    if (!product) return {};
    return {
      mixer_machine_id: product.MixerMachine,
      processing_machine_id: product.ProcessingMachine,
      oven_machine_id: product.OvenMachine,
      mixing_workers: workersFor(product.ID, product.MixerMachine) ?? 1,
      processing_workers: workersFor(product.ID, product.ProcessingMachine) ?? 1,
      baking_workers: workersFor(product.ID, product.OvenMachine) ?? 1,
    };
  };

  // Cache of phase-derived device/worker defaults per product, so picking the
  // same product twice doesn't refetch its phases.
  const phaseDefaultsCache = useRef<Map<number, Partial<DailyPlanEntry>>>(new Map());

  // Map a product's ProductionPhase rows → daily-plan device/worker defaults.
  // mixing → mixer, processing → obrada, baking → pećnica.
  const derivePhaseDefaults = (phases: ProductionPhase[]): Partial<DailyPlanEntry> => {
    const byCode = (code: string) =>
      phases.find((p) => p.ProductionPhaseType_ProductionPhaseToPhaseType?.Code === code);
    const mixing = byCode("mixing");
    const processing = byCode("processing");
    const baking = byCode("baking");
    const defaults: Partial<DailyPlanEntry> = {};
    if (mixing?.Machine != null) defaults.mixer_machine_id = mixing.Machine;
    if (processing?.Machine != null) defaults.processing_machine_id = processing.Machine;
    if (baking?.Machine != null) defaults.oven_machine_id = baking.Machine;
    if (mixing?.Workers != null) defaults.mixing_workers = mixing.Workers;
    if (processing?.Workers != null) defaults.processing_workers = processing.Workers;
    if (baking?.Workers != null) defaults.baking_workers = baking.Workers;
    return defaults;
  };

  // Merge phase-derived defaults into every plan row of the given product,
  // without clobbering values the user may have already chosen for devices.
  const mergePhaseDefaults = (productId: number, defaults: Partial<DailyPlanEntry>) => {
    if (Object.keys(defaults).length === 0) return;
    setDailyPlan((prev) =>
      prev.map((row) =>
        row.product_id === productId
          ? {
              ...row,
              mixer_machine_id: row.mixer_machine_id ?? defaults.mixer_machine_id,
              processing_machine_id: row.processing_machine_id ?? defaults.processing_machine_id,
              oven_machine_id: row.oven_machine_id ?? defaults.oven_machine_id,
              // Phase-configured worker counts take precedence over the generic
              // "1" fallback from productPlanDefaults.
              mixing_workers: defaults.mixing_workers ?? row.mixing_workers,
              processing_workers: defaults.processing_workers ?? row.processing_workers,
              baking_workers: defaults.baking_workers ?? row.baking_workers,
            }
          : row
      )
    );
  };

  // Pull the product's phase-configured devices/workers into its plan rows.
  const applyPhaseDefaults = async (productId: number) => {
    if (!productId) return;
    const cached = phaseDefaultsCache.current.get(productId);
    if (cached) {
      mergePhaseDefaults(productId, cached);
      return;
    }
    try {
      const phases = await mesApi.getProductPhases(productId);
      const defaults = derivePhaseDefaults(phases || []);
      phaseDefaultsCache.current.set(productId, defaults);
      mergePhaseDefaults(productId, defaults);
    } catch {
      // Keep the legacy-column defaults if phases can't be loaded.
    }
  };

  // Date of the load currently in flight; a response for any other date is
  // discarded so quickly switching days can't leave one day's plan on another.
  const loadingDateRef = useRef<string>(selectedDate);

  const loadProductionDay = async (date: string) => {
    try {
      const data = await mesApi.getProductionDay(date);
      if (loadingDateRef.current !== date) return;
      setProductionDay(data);
    } catch (err: any) {
      if (loadingDateRef.current !== date) return;
      setProductionDay(null);
    }
  };

  const loadTimelineForDate = async (date: string) => {
    loadingDateRef.current = date;
    setEditingConfirmed(false);
    loadProductionDay(date);
    try {
      const data = await mesApi.getTimelineByDate(date);
      if (loadingDateRef.current !== date) return;
      setTimeline(data);
      setShiftStart(data?.ShiftStartTime ?? "");
      if (data?.TimelineBatch?.length) {
        setGeneratedBatches(data.TimelineBatch);
        setSemiFinishedDates(collectSemiFinishedDateOverrides(data.TimelineBatch));
        // Only a draft is edited through step 1; a confirmed plan seeds the rows
        // from startEditingConfirmed when the planner enters edit mode.
        const plan =
          data.Status === "draft" ? batchesToDailyPlan(data.TimelineBatch) : [];
        setDailyPlan(plan.length > 0 ? plan : createEmptyDailyPlan());
      } else {
        setGeneratedBatches([]);
        setSemiFinishedDates({});
        setDailyPlan(createEmptyDailyPlan());
      }
      if (!data) setModifications([]);
      setWizardStep(1);
      setPlanDirty(false);
    } catch (err: any) {
      if (loadingDateRef.current !== date) return;
      setTimeline(null);
      setGeneratedBatches([]);
      setDailyPlan(createEmptyDailyPlan());
      setWizardStep(1);
      setPlanDirty(false);
    }
  };

  const isDraftMode = !timeline || timeline.Status === "draft";
  const canEditPlan = isDraftMode || editingConfirmed;

  /**
   * Reschedule a reordered batch list on the server and show the result.
   *
   * This used to be done in the browser (recalculateBatchTimesLocally). That
   * copy of the algorithm put every batch of a mixer on the selected date and
   * knew nothing about other plans using the same mixer that day, so it drifted
   * from the engine — and its output was what got saved. There is now one
   * scheduler, on the server.
   */
  const previewSchedule = async (
    batches: Partial<TimelineBatch>[]
  ): Promise<Partial<TimelineBatch>[]> => {
    try {
      return await mesApi.scheduleTimelinePreview({
        batches,
        productionDate: selectedDate,
        shiftStartTime: shiftStart || null,
        excludeTimelineId: timeline?.ID ?? null,
      });
    } catch (err: any) {
      toast.error("Greška pri preračunu rasporeda: " + err.message);
      return batches;
    }
  };

  const persistBatchOrder = async (
    reordered: Partial<TimelineBatch>[],
    options?: { recalcTimes?: boolean }
  ) => {
    // While editing a confirmed plan in place, keep the reorder local — the new
    // order is persisted only when the edit is confirmed as a revision.
    if (editingConfirmed) {
      setGeneratedBatches(await previewSchedule(reordered));
      return;
    }
    if (!timeline?.ID) {
      setGeneratedBatches(await previewSchedule(reordered));
      toast.info("Raspored ažuriran. Spremi draft za sinkronizaciju s bazom.");
      return;
    }
    // Freshly generated batches carry a NEGATIVE placeholder id (assignBatchKeys)
    // until they are saved — only real DB ids may be sent to the reorder route,
    // otherwise it tries to update rows that don't exist.
    const withIds = reordered.filter((b) => (b.ID ?? 0) > 0);
    if (withIds.length !== reordered.length) {
      setGeneratedBatches(await previewSchedule(reordered));
      toast.info("Raspored ažuriran. Spremi plan za sinkronizaciju s bazom.");
      return;
    }

    setLoading(true);
    try {
      const batchOrder = withIds.map((b, i) => ({
        batch_id: b.ID!,
        redoslijed: b.Sequence ?? i + 1,
      }));
      await mesApi.reorderBatches(timeline.ID, batchOrder);
      const refreshed = await mesApi.getTimeline(timeline.ID);

      let batches: Partial<TimelineBatch>[];
      if (options?.recalcTimes) {
        // Mixer/mass edits live only in memory until the plan is saved again,
        // so the stored rows the reorder route just rescheduled are stale.
        // Preview the in-memory batches to show the times the edit produces.
        batches = await previewSchedule(reordered);
      } else {
        // Backend (reorder ruta) je već preračunao faze za novi redoslijed —
        // koristi ta vremena kao izvor istine.
        batches = refreshed.TimelineBatch || reordered;
      }

      setTimeline({
        ...refreshed,
        TimelineBatch: batches as TimelineBatch[],
      });
      setGeneratedBatches(batches);
      toast.success("Raspored preračunat");
    } catch (err: any) {
      toast.error("Greška pri ažuriranju rasporeda: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyMixerEdits = async (updated: Partial<TimelineBatch>[]) => {
    await persistBatchOrder(updated, { recalcTimes: true });
  };

  const handleGanttReorder = async (draggedBatchId: number, targetBatchId: number) => {
    // The resource groupings draw one machine's slice of a batch per row, so
    // they expose no sequence to swap and the chart disables dragging there.
    if (isGanttResourceGrouping(ganttGroupBy)) return;
    const reordered = reorderBatchInGroup(
      generatedBatches,
      draggedBatchId,
      targetBatchId,
      ganttGroupBy
    );
    await persistBatchOrder(reordered);
  };

  // Enter edit mode for a confirmed plan: seed the editable wizard from the
  // confirmed batches without touching the DB. The user can now add/remove/
  // change products and reorder; confirming creates a new revision.
  const startEditingConfirmed = () => {
    const source = timeline?.TimelineBatch?.length
      ? timeline.TimelineBatch
      : generatedBatches;
    setGeneratedBatches(source);
    setSemiFinishedDates(collectSemiFinishedDateOverrides(source));
    const plan = batchesToDailyPlan(source);
    setDailyPlan(plan.length > 0 ? plan : createEmptyDailyPlan());
    setModifications([]);
    setEditingConfirmed(true);
    setWizardStep(1);
    setPlanDirty(false);
    setSidebarOpen(true);
    toast.info("Uređivanje plana — promijeni proizvode i raspored, zatim potvrdi novu reviziju");
  };

  const cancelEditingConfirmed = () => {
    setEditingConfirmed(false);
    setRevisionReason("");
    loadTimelineForDate(selectedDate);
  };

  // Confirm the edited plan as a new revision (supersedes the previous version).
  const handleConfirmEditedRevision = async () => {
    if (!timeline?.ID) return;
    if (generatedBatches.length === 0) {
      toast.error("Nema zamjesa za spremiti");
      return;
    }
    setLoading(true);
    try {
      const revised = await mesApi.createTimelineRevision(
        timeline.ID,
        revisionReason || "Izmjena plana",
        generatedBatches
      );
      setEditingConfirmed(false);
      setRevisionReason("");
      await loadTimelineForDate(selectedDate);
      toast.success(`Nova revizija potvrđena (verzija ${revised.Version})`);
    } catch (err: any) {
      toast.error("Greška pri reviziji: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== Operativna izmjena potvrđenog plana =====
  const handleConfirmedStatusChange = async (
    batch: Partial<TimelineBatch>,
    newStatus: BatchStatus
  ) => {
    if (!timeline?.ID || !batch.ID) return;
    const oldStatus = (batch.BatchStatus as BatchStatus) || "planned";
    if (newStatus === oldStatus) return;

    setLoading(true);
    try {
      await mesApi.updateBatchStatus(batch.ID, newStatus);
      await mesApi.logDispatchOverride({
        Timeline: timeline.ID,
        TimelineBatch: batch.ID,
        OverrideType: "STATUS_CHANGE",
        OldStatus: oldStatus,
        NewStatus: newStatus,
      });
      await loadTimelineForDate(selectedDate);
      toast.success("Status zamjesa ažuriran");
    } catch (err: any) {
      toast.error("Greška pri promjeni statusa: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOperationalReorder = async (
    batchOrder: { batch_id: number; redoslijed: number }[],
    reason: string
  ) => {
    if (!timeline?.ID) return;
    setLoading(true);
    try {
      await mesApi.operationalReorder(timeline.ID, batchOrder, reason);
      await loadTimelineForDate(selectedDate);
      toast.success("Operativna izmjena primijenjena");
    } catch (err: any) {
      toast.error("Greška pri operativnoj izmjeni: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    setDailyPlan([...dailyPlan, emptyPlanRow()]);
    setPlanDirty(true);
  };

  const handleRemoveRow = (index: number) => {
    const newPlan = dailyPlan.filter((_, i) => i !== index);
    setDailyPlan(newPlan);
    setPlanDirty(true);
  };

  const handleAddProductFromSidebar = (product: Product) => {
    // Check if product already exists in plan
    const existingIndex = dailyPlan.findIndex(
      (row) => row.product_id === product.ID
    );

    if (existingIndex >= 0) {
      // Increment batch count
      const newPlan = [...dailyPlan];
      newPlan[existingIndex].batch_count += 1;
      setDailyPlan(newPlan);
      toast.success(`Increased ${product.ProductName} batch count`);
    } else {
      // Filter out any empty rows and add new product
      const filteredPlan = dailyPlan.filter(row => row.product_id !== 0);
      setDailyPlan([
        ...filteredPlan,
        {
          product_id: product.ID,
          product_name: product.ProductName,
          batch_count: 1,
          mass_kg: product.DefaultBatchMassKg || 0,
          ...productPlanDefaults(product),
        },
      ]);
      void applyPhaseDefaults(product.ID);
      toast.success(`Added ${product.ProductName} to plan`);
    }
    setPlanDirty(true);
  };

  const numericPlanFields = new Set<keyof DailyPlanEntry>([
    "batch_count",
    "mass_kg",
    "mixer_machine_id",
    "processing_machine_id",
    "oven_machine_id",
    "mixing_workers",
    "processing_workers",
    "baking_workers",
  ]);

  const handlePlanChange = (index: number, field: keyof DailyPlanEntry, value: any) => {
    const newPlan = [...dailyPlan];
    if (field === "product_id") {
      const product = products.find((p) => p.ID === Number(value));
      newPlan[index] = {
        ...newPlan[index],
        product_id: Number(value),
        product_name: product?.ProductName || "",
        mass_kg: product?.DefaultBatchMassKg || 0,
        ...productPlanDefaults(product),
      };
      if (product) void applyPhaseDefaults(product.ID);
    } else if (numericPlanFields.has(field)) {
      (newPlan[index] as any)[field] = value === "" ? undefined : Number(value);
    } else {
      (newPlan[index] as any)[field] = value;
    }
    setDailyPlan(newPlan);
    setPlanDirty(true);
  };

  /**
   * Change the start of the production day for this plan.
   *
   * An unsaved plan only needs the value in state — it is sent along with the
   * next generate/save. A saved one is updated server-side right away, because
   * moving the start of day reschedules the whole day (including semi-finished
   * batches other plans put on it) and only the server can do that coherently.
   */
  const handleShiftStartChange = async (value: string) => {
    setShiftStart(value);
    if (!timeline?.ID) {
      setPlanDirty(true);
      return;
    }
    setLoading(true);
    try {
      const updated = await mesApi.setTimelineShiftStart(timeline.ID, value || null);
      setTimeline(updated);
      if (updated?.TimelineBatch?.length) setGeneratedBatches(updated.TimelineBatch);
      loadProductionDay(selectedDate);
      toast.success(value ? `Dan počinje u ${value}` : "Vraćeno na globalnu postavku");
    } catch (err: any) {
      toast.error("Greška pri promjeni početka dana: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTimeline = async (): Promise<boolean> => {
    const validPlan = dailyPlan.filter((p) => p.product_id > 0);
    if (validPlan.length === 0) {
      toast.error("Dodajte barem jedan proizvod u plan");
      return false;
    }

    setLoading(true);
    try {
      const mods = modifications.length
        ? toTimelineModificationPayload(modifications)
        : undefined;
      const result = await mesApi.generateTimeline(validPlan, mods, selectedDate, shiftStart || null);
      setGeneratedBatches(
        applySemiFinishedDateOverrides(assignBatchKeys(result.batches), semiFinishedDates)
      );
      setPlanDirty(false);
      toast.success("Timeline generiran");
      return true;
    } catch (err: any) {
      toast.error("Failed to generate timeline: " + err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ===== Wizard navigation =====
  const hasValidProduct = dailyPlan.some((p) => p.product_id > 0);

  // Ensure a freshly generated schedule exists before leaving step 1.
  const ensureSchedule = async (): Promise<boolean> => {
    if (generatedBatches.length === 0 || planDirty) {
      return handleGenerateTimeline();
    }
    return true;
  };

  const goToSemiFinished = async () => {
    if (await ensureSchedule()) setWizardStep(2);
  };

  const goToSchedule = async () => {
    if (await ensureSchedule()) setWizardStep(3);
  };

  const goToGantt = async () => {
    if (await ensureSchedule()) setWizardStep(4);
  };

  const handleStepClick = (id: number) => {
    if (id === wizardStep) return;
    if (id === 1) setWizardStep(1);
    else if (id === 2) goToSemiFinished();
    else if (id === 3) goToSchedule();
    else if (id === 4) goToGantt();
  };

  // Apply an edited production day to all batches of a semi-finished product,
  // and remember it so a later regeneration doesn't revert to the default rule.
  const handleSemiFinishedDateChange = (key: number, productionDate: string) => {
    setSemiFinishedDates((prev) => ({ ...prev, [key]: productionDate }));
    setGeneratedBatches((prev) =>
      applySemiFinishedDateEdits(prev, [{ key, productionDate }])
    );
  };

  const handleSaveTimeline = async () => {
    if (generatedBatches.length === 0) {
      toast.error("No batches to save");
      return;
    }

    setLoading(true);
    try {
      const newTimeline = await mesApi.createTimeline({
        ProductionDate: selectedDate,
        batches: generatedBatches,
        Notes: "Draft timeline",
        ShiftStartTime: shiftStart || null,
      });
      setTimeline(newTimeline);
      // Adopt the persisted batches so they carry real DB ids; without this the
      // in-memory batches keep their negative placeholder ids and a later
      // reorder can't be saved.
      if (newTimeline?.TimelineBatch?.length) {
        setGeneratedBatches(newTimeline.TimelineBatch);
      }
      loadProductionDay(selectedDate);
      toast.success("Timeline saved as draft");
    } catch (err: any) {
      toast.error("Failed to save timeline: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLotAssignment = () => {
    if (!timeline || !timeline.TimelineBatch) {
      toast.error("No batches to assign lots to");
      return;
    }
    setConfirmModalOpen(false);
    setLotAssignmentModalOpen(true);
  };

  /**
   * Dodjela lotova je spremljena. Kad se ekran otvorio iz potvrde skice, plan
   * se tek sad potvrđuje; nad već potvrđenim planom (dan potvrđen bez lotova
   * ili revizija koja je donijela nove zamjese) samo se osvježi prikaz, jer
   * ponovna potvrda bi plan bespotrebno prepisala.
   */
  const handleLotAssignmentComplete = async () => {
    if (!timeline) return;

    setLotAssignmentModalOpen(false);

    if (timeline.Status === "confirmed") {
      await loadTimelineForDate(selectedDate);
      toast.success("Lotovi dodijeljeni potvrđenom planu");
      return;
    }

    setLoading(true);
    try {
      const confirmed = await mesApi.confirmTimeline(timeline.ID, confirmNotes);
      setTimeline(confirmed);
      setConfirmNotes("");
      loadProductionDay(selectedDate);
      toast.success("Timeline confirmed with lot assignments");
    } catch (err: any) {
      toast.error("Failed to confirm timeline: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipLotAssignment = async () => {
    if (!timeline) return;

    setConfirmModalOpen(false);
    setLoading(true);
    try {
      const confirmed = await mesApi.confirmTimeline(timeline.ID, confirmNotes);
      setTimeline(confirmed);
      setConfirmNotes("");
      loadProductionDay(selectedDate);
      toast.success("Timeline confirmed (without lot assignments)");
    } catch (err: any) {
      toast.error("Failed to confirm timeline: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr?: string) => formatClockTime(dateStr);

  const filteredProducts = products.filter((p) =>
    p.ProductName.toLowerCase().includes(productSearchTerm.toLowerCase()) &&
    p.IsActive !== false
  );

  const modificationProductNames =
    getUniqueProductNames(generatedBatches).length > 0
      ? getUniqueProductNames(generatedBatches)
      : dailyPlan.filter((p) => p.product_name).map((p) => p.product_name);

  const modificationMixers =
    getUniqueMixers(generatedBatches).length > 0
      ? getUniqueMixers(generatedBatches)
      : products
          .filter((p) => dailyPlan.some((d) => d.product_id === p.ID))
          .map(
            (p) =>
              p.Machine_MachineToProduct_MixerMachine
                ?.MachineName
          )
          .filter(Boolean) as string[];

  const toGanttBatches = (list: Partial<TimelineBatch>[]): TimelineBatch[] =>
    list.map((b, i) => ({
      ...(b as TimelineBatch),
      ID: b.ID || getBatchKey(b, i),
      BatchStatus: (b.BatchStatus || "planned") as TimelineBatch["BatchStatus"],
      Sequence: b.Sequence ?? i + 1,
    }));

  // Batches physically produced on the selected day (semi-finished destined for
  // other days are shown on those days instead).
  const sameDayBatches = generatedBatches.filter((b) =>
    batchActiveOnDay(b, selectedDate)
  );
  const offDayBatches = generatedBatches.filter(
    (b) => !batchActiveOnDay(b, selectedDate)
  );

  const renderPhaseDayStrip = (batches: Partial<TimelineBatch>[]) => {
    const columns = collectPhaseDays(batches);
    if (columns.length < 2) return null;
    return (
      <div className="phase-day-strip">
        {columns.map((column) => (
          <div key={column.date} className="phase-day-col">
            <div className="phase-day-label">{formatHrDay(column.date)}</div>
            <ul>
              {column.items.map(({ batch, phase }, idx) => (
                <li key={`${batch.ID ?? batch.MixCode}-${phase.code}-${phase.start}-${idx}`}>
                  <span className="phase-day-dot" style={{ background: phaseColor(phase.code) }} />
                  <span className="phase-day-name">{phase.name || phaseName(phase.code)}</span>
                  <span className="phase-day-time">
                    {formatTime(phase.start)}–{formatTime(phase.end)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const renderViewToggle = () => (
    <div className="view-toggle">
      <button
        type="button"
        className={timelineView === "gantt" ? "active" : ""}
        onClick={() => setTimelineView("gantt")}
      >
        Gantt
      </button>
      <button
        type="button"
        className={timelineView === "table" ? "active" : ""}
        onClick={() => setTimelineView("table")}
      >
        Tablica
      </button>
    </div>
  );

  const statusLabel = (status?: string): string =>
    status === "completed" ? "Zavrseno" :
    status === "in_progress" ? "U tijeku" :
    status === "ready" ? "Spremno" :
    status === "on_hold" ? "Pauzirano" :
    status === "skipped" ? "Preskoceno" :
    status === "canceled" ? "Otkazano" : "Planirano";

  const renderTimelineTable = (
    batches: Partial<TimelineBatch>[],
    options?: { editableStatus?: boolean }
  ) => (
    <div className="mes-table-wrap">
      <table className="mes-table timeline-table">
      <thead>
        <tr>
          <th>#</th>
          <th>ID Zamjesa</th>
          <th>Proizvod</th>
          <th>Mikser</th>
          <th>Pećnica</th>
          <th>Masa (kg)</th>
          <th title="Broj ljudi: miješanje / obrada / pečenje">Ljudi (M/O/P)</th>
          <th>Pocetak</th>
          <th>Kraj</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {batches.map((batch, index) => (
          <tr key={index} className={`batch-row status-${batch.BatchStatus || "planned"}`}>
            <td>{batch.Sequence || index + 1}</td>
            <td><code className="batch-id-code">{batch.MixCode}</code></td>
            <td className="batch-product">{batch.ProductName}</td>
            <td><span className="mixer-badge">{batch.MixerName}</span></td>
            <td>{batch.OvenName || "-"}</td>
            <td>{batch.FinalMassKg}</td>
            <td>
              {batch.MixingWorkers ?? "-"} / {batch.ProcessingWorkers ?? "-"} / {batch.BakingWorkers ?? "-"}
            </td>
            <td>{formatTime(batch.MixingStart)}</td>
            <td>{formatTime(batch.BakingEnd)}</td>
            <td>
              {options?.editableStatus && batch.ID ? (
                <select
                  className="input-classic input-sm status-select"
                  value={batch.BatchStatus || "planned"}
                  disabled={loading}
                  onChange={(e) =>
                    handleConfirmedStatusChange(batch, e.target.value as BatchStatus)
                  }
                >
                  {BATCH_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{statusLabel(s)}</option>
                  ))}
                </select>
              ) : (
                <span className={`status-pill status-${batch.BatchStatus || "planned"}`}>
                  <span className="status-dot"></span>
                  {statusLabel(batch.BatchStatus)}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );

  const renderTimelineView = (
    allowReorder: boolean,
    batches: Partial<TimelineBatch>[],
    options?: { editableStatus?: boolean }
  ) =>
    timelineView === "gantt" ? (
      <GanttPlannerChart
        batches={toGanttBatches(batches)}
        selectedDate={selectedDate}
        clipToDate={selectedDate}
        groupBy={ganttGroupBy}
        onGroupByChange={setGanttGroupBy}
        machines={machines}
        onReorder={allowReorder ? handleGanttReorder : undefined}
      />
    ) : (
      renderTimelineTable(batches, options)
    );

  const fmtDay = (value?: string | null): string | undefined => {
    if (!value) return undefined;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return undefined;
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Read-only panel: everything physically produced on the selected day,
  // including semi-finished made today for other days' plans (and excluding
  // this day's semi-finished that are made earlier).
  const renderProductionDayPanel = () => {
    const batches = productionDay?.batches ?? [];
    if (batches.length === 0) return null;

    const inbound = batches.filter(
      (b) => b.IsSemiFinished && fmtDay(b.PlanProductionDate) !== selectedDate
    );
    const continuation = batches.filter(
      (b) => !b.IsSemiFinished && fmtDay(b.ProductionDate) !== selectedDate
    );

    return (
      <div className="mes-section production-day-panel">
        <div className="timeline-section-header">
          <h3 style={{ margin: 0 }}>Proizvodnja na ovaj dan</h3>
          <span className="pd-count">
            {batches.length} zamjesa
            {inbound.length > 0 ? ` · ${inbound.length} polugotovih za druge dane` : ""}
            {continuation.length > 0 ? ` · ${continuation.length} višednevnih` : ""}
          </span>
        </div>
        <p className="editor-hint" style={{ marginTop: 0 }}>
          Sve što se fizički radi {selectedDate} — uključuje faze višednevnih zamjesa
          (npr. prvo miješanje jedan dan, pakiranje drugi) i polugotove namijenjene
          drugim danima.
        </p>
        <GanttPlannerChart
          batches={toGanttBatches(batches)}
          selectedDate={selectedDate}
          clipToDate={selectedDate}
          title={`Faze ${formatHrDay(selectedDate)}`}
          groupBy={ganttGroupBy}
          onGroupByChange={setGanttGroupBy}
          machines={machines}
        />
        <div className="mes-table-wrap">
          <table className="mes-table">
          <thead>
            <tr>
              <th>ID Zamjesa</th>
              <th>Proizvod</th>
              <th>Vrsta</th>
              <th>Faze danas</th>
              <th>Početak</th>
              <th>Kraj</th>
              <th>Namjena</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch, index) => {
              const planDay = fmtDay(batch.PlanProductionDate);
              const prodDay = fmtDay(batch.ProductionDate);
              const isInbound = !!batch.IsSemiFinished && planDay !== selectedDate;
              const isContinuation = !batch.IsSemiFinished && prodDay !== selectedDate;
              const todayPhases = batchPhaseRefs(batch)
                .map((phase) => clipPhaseToDay(phase, selectedDate))
                .filter((phase): phase is NonNullable<typeof phase> => phase != null);
              const start = todayPhases[0]?.start ?? batch.MixingStart;
              const end = todayPhases[todayPhases.length - 1]?.end ?? batch.BakingEnd;
              return (
                <tr
                  key={batch.ID ?? index}
                  className={isInbound || isContinuation ? "pd-inbound" : ""}
                >
                  <td><code className="batch-id-code">{batch.MixCode}</code></td>
                  <td>{batch.ProductName}</td>
                  <td>
                    {batch.IsSemiFinished ? (
                      <span className="pd-badge pd-semi">Polugotov</span>
                    ) : (
                      <span className="pd-badge pd-final">Gotov</span>
                    )}
                  </td>
                  <td>
                    {todayPhases.length > 0 ? (
                      <div className="pd-phase-list">
                        {todayPhases.map((phase, i) => (
                          <span key={`${phase.code}-${i}`} className="pd-phase-chip">
                            <span className="phase-day-dot" style={{ background: phaseColor(phase.code) }} />
                            {phase.name || phaseName(phase.code)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{formatTime(start)}</td>
                  <td>{formatTime(end)}</td>
                  <td>
                    {isInbound && planDay ? (
                      <span className="pd-target">za {planDay}</span>
                    ) : isContinuation && prodDay ? (
                      <span className="pd-target">nastavak · plan {prodDay}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  return (
    <div className="mes-page">
      <Header title="MES - Planiranje" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content planning-page" style={{ display: "flex", gap: "20px", position: "relative" }}>
          {/* Main Content Area */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Date Selector */}
            <MesDateSelector
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              showTimelineIndicators={true}
            />

          {/* Timeline Status */}
          {timeline && (
            <div className={`mes-status-banner ${timeline.Status}`}>
              Plan {fmtDay(timeline.ProductionDate) || selectedDate}:{" "}
              <strong>{timeline.Status.toUpperCase()}</strong>
              {fmtDay(timeline.ProductionDate) &&
              fmtDay(timeline.ProductionDate) !== selectedDate
                ? ` — prikaz faza za ${selectedDate}`
                : ""}
              {timeline.Status === "confirmed" && (
                <span> (verzija {timeline.Version})</span>
              )}
            </div>
          )}

          {renderProductionDayPanel()}

          {/* ===== Draft: 3-step creation wizard ===== */}
          {canEditPlan && (
            <>
              <PlanWizardStepper
                steps={WIZARD_STEPS}
                current={wizardStep}
                maxReachable={hasValidProduct ? 4 : 1}
                onStepClick={handleStepClick}
              />

              {/* Step 1 — Proizvodi */}
              {wizardStep === 1 && (
            <div className="mes-section planning-section">
              <h3 style={{ marginTop: 0 }}>1. Definiranje proizvoda</h3>
              <p className="editor-hint" style={{ marginTop: 0 }}>
                Odaberi proizvode, broj zamjesa, masu, uređaje (mikser / obrada / pećnica) i broj ljudi po fazi.
              </p>

              <div className="planning-day-start">
                <label htmlFor="shift-start-input">Početak dana (prvo miješanje)</label>
                <input
                  id="shift-start-input"
                  type="time"
                  value={shiftStart}
                  placeholder={globalShiftStart}
                  onChange={(e) => handleShiftStartChange(e.target.value)}
                />
                <span className="editor-hint">
                  {shiftStart
                    ? `Vrijedi samo za ${selectedDate}.`
                    : `Zadano: ${globalShiftStart || "04:00"} (globalna postavka).`}
                </span>
                {shiftStart && (
                  <button type="button" className="link-button" onClick={() => handleShiftStartChange("")}>
                    Vrati na zadano
                  </button>
                )}
              </div>
              <div style={{ overflowX: "auto" }}>
              <table className="mes-table plan-table">
                <thead>
                  <tr>
                    <th>Proizvod</th>
                    <th>Broj zamjesa</th>
                    <th>Masa (kg)</th>
                    <th>Mikser</th>
                    <th>Obrada</th>
                    <th>Pećnica</th>
                    <th title="Broj ljudi za miješanje">Ljudi miješanje</th>
                    <th title="Broj ljudi za obradu">Ljudi obrada</th>
                    <th title="Broj ljudi za pečenje">Ljudi pečenje</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {dailyPlan.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <select
                          value={String(row.product_id)}
                          onChange={(e) =>
                            handlePlanChange(index, "product_id", e.target.value)
                          }
                          className="input-classic"
                        >
                          <option value="0">Odaberi proizvod</option>
                          {products.map((p) => (
                            <option key={p.ID} value={p.ID}>{p.ProductName}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min={1}
                          value={String(row.batch_count)}
                          onChange={(e) =>
                            handlePlanChange(index, "batch_count", e.target.value)
                          }
                          className="input-classic input-sm"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={String(row.mass_kg ?? "")}
                          onChange={(e) =>
                            handlePlanChange(index, "mass_kg", e.target.value)
                          }
                          className="input-classic input-sm"
                        />
                      </td>
                      <td>
                        <select
                          value={String(row.mixer_machine_id ?? "")}
                          onChange={(e) =>
                            handlePlanChange(index, "mixer_machine_id", e.target.value)
                          }
                          className="input-classic"
                          disabled={!row.product_id}
                        >
                          <option value="">—</option>
                          {machinesForRole("mixer").map((m) => (
                            <option key={m.ID} value={m.ID}>{m.MachineName}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={String(row.processing_machine_id ?? "")}
                          onChange={(e) =>
                            handlePlanChange(index, "processing_machine_id", e.target.value)
                          }
                          className="input-classic"
                          disabled={!row.product_id}
                        >
                          <option value="">—</option>
                          {machinesForRole("processor").map((m) => (
                            <option key={m.ID} value={m.ID}>{m.MachineName}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={String(row.oven_machine_id ?? "")}
                          onChange={(e) =>
                            handlePlanChange(index, "oven_machine_id", e.target.value)
                          }
                          className="input-classic"
                          disabled={!row.product_id}
                        >
                          <option value="">—</option>
                          {machinesForRole("oven").map((m) => (
                            <option key={m.ID} value={m.ID}>{m.MachineName}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          value={String(row.mixing_workers ?? "")}
                          onChange={(e) =>
                            handlePlanChange(index, "mixing_workers", e.target.value)
                          }
                          className="input-classic input-sm"
                          disabled={!row.product_id}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          value={String(row.processing_workers ?? "")}
                          onChange={(e) =>
                            handlePlanChange(index, "processing_workers", e.target.value)
                          }
                          className="input-classic input-sm"
                          disabled={!row.product_id}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          value={String(row.baking_workers ?? "")}
                          onChange={(e) =>
                            handlePlanChange(index, "baking_workers", e.target.value)
                          }
                          className="input-classic input-sm"
                          disabled={!row.product_id}
                        />
                      </td>
                      <td>
                        <button
                          className="btn-icon-danger"
                          onClick={() => handleRemoveRow(index)}
                        >
                          X
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <div className="mes-actions wizard-nav">
                <OutlinedButton onClick={handleAddRow}>+ Dodaj red</OutlinedButton>
                <span className="wizard-nav-spacer" />
                <PrimaryButton onSubmit={goToSemiFinished} loading={loading}>
                  Dalje: Polugotovi →
                </PrimaryButton>
              </div>
            </div>
              )}

              {/* Step 2 — Polugotovi proizvodi (raspored po danima) */}
              {wizardStep === 2 && (
                <>
                  <SemiFinishedScheduleStep
                    batches={generatedBatches}
                    parentDate={selectedDate}
                    onChangeDate={handleSemiFinishedDateChange}
                    disabled={loading}
                  />
                  <div className="mes-section">
                    <div className="mes-actions wizard-nav">
                      <OutlinedButton onClick={() => setWizardStep(1)}>← Natrag</OutlinedButton>
                      <span className="wizard-nav-spacer" />
                      <PrimaryButton onSubmit={goToSchedule} loading={loading}>
                        Dalje: Raspored →
                      </PrimaryButton>
                    </div>
                  </div>
                </>
              )}

              {/* Step 3 — Raspored (po proizvodu, sve faze) */}
              {wizardStep === 3 && (
                <>
                  <div className="mes-section planning-section">
                    <h3 style={{ marginTop: 0 }}>3. Uređivanje rasporeda</h3>
                    <p className="editor-hint" style={{ marginTop: 0 }}>
                      Opcionalna pravila redoslijeda (prioritet / noć / cik-cak). Nakon izmjene pravila
                      regeneriraj raspored.
                    </p>
                    <TimelineModificationsPanel
                      modifications={modifications}
                      onChange={setModifications}
                      productNames={modificationProductNames}
                      mixers={modificationMixers.length ? modificationMixers : ["Diosna", "Talijan"]}
                    />
                    <div className="mes-actions">
                      <OutlinedButton onClick={() => handleGenerateTimeline()} disabled={loading}>
                        ↻ Regeneriraj raspored
                      </OutlinedButton>
                    </div>
                  </div>

                  <TimelinePhaseEditor
                    batches={generatedBatches}
                    onApply={handleApplyMixerEdits}
                    disabled={loading}
                    formatTime={formatTime}
                  />

                  <div className="mes-section">
                    <div className="mes-actions wizard-nav">
                      <OutlinedButton onClick={() => setWizardStep(2)}>← Natrag</OutlinedButton>
                      <span className="wizard-nav-spacer" />
                      <PrimaryButton onSubmit={goToGantt} loading={loading}>
                        Dalje: Gantt →
                      </PrimaryButton>
                    </div>
                  </div>
                </>
              )}

              {/* Step 4 — Gantt */}
              {wizardStep === 4 && (
                <div className="mes-section timeline-section">
                  <div className="timeline-section-header">
                    <h3 style={{ marginTop: 0 }}>4. Gantt — {selectedDate}</h3>
                    {renderViewToggle()}
                  </div>

                  {offDayBatches.length > 0 && (
                    <p className="editor-hint" style={{ marginTop: 0 }}>
                      Prikazani su zamjesi koji se rade na {selectedDate}. {offDayBatches.length} polugotovih
                      zamjesa raspoređeno je na druge dane (vidi „Polugotovi proizvodi").
                    </p>
                  )}

                  {renderPhaseDayStrip(sameDayBatches)}
                  {renderTimelineView(true, sameDayBatches)}

                  <div className="mes-actions wizard-nav">
                    <OutlinedButton onClick={() => setWizardStep(3)}>← Natrag</OutlinedButton>
                    <span className="wizard-nav-spacer" />
                    {editingConfirmed ? (
                      <>
                        <OutlinedButton onClick={cancelEditingConfirmed}>
                          Odustani od izmjene
                        </OutlinedButton>
                        <PrimaryButton onSubmit={handleConfirmEditedRevision} loading={loading}>
                          Potvrdi izmjene (nova revizija)
                        </PrimaryButton>
                      </>
                    ) : (
                      <>
                        {!timeline && (
                          <PrimaryButton onSubmit={handleSaveTimeline} loading={loading}>
                            Spremi kao draft
                          </PrimaryButton>
                        )}
                        {timeline?.Status === "draft" && (
                          <PrimaryButton onSubmit={() => setConfirmModalOpen(true)} loading={loading}>
                            Potvrdi timeline
                          </PrimaryButton>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ===== Confirmed timeline (read-only) ===== */}
          {timeline?.Status === "confirmed" && !editingConfirmed && (
            <>
              <div className="mes-section revision-section">
                <h3>Potvrđeni plan</h3>
                <p>
                  Plan je potvrđen (verzija {timeline.Version}). Promijeni status zamjesa ili
                  napravi operativnu izmjenu redoslijeda u hodu; za veće izmjene (dodavanje,
                  uklanjanje ili promjena proizvoda) uredi plan i potvrdi novu reviziju.
                </p>
                {/* Dodjela lotova je dostupna i nakon potvrde: dan potvrđen bez
                    lotova i revizija koja donese nove zamjese inače ostave
                    mikser bez lota, a ekran za dodjelu je prije živio samo na
                    putu potvrde skice. */}
                <div className="revision-form">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleOpenLotAssignment}
                    disabled={loading}
                  >
                    Dodijeli lotove
                  </button>
                </div>
                <div className="revision-form">
                  <input
                    className="input-classic"
                    placeholder="Razlog revizije (opcionalno)"
                    value={revisionReason}
                    onChange={(e) => setRevisionReason(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={startEditingConfirmed}
                    disabled={loading}
                  >
                    Izmijeni plan (nova revizija)
                  </button>
                </div>
              </div>

              {generatedBatches.length > 0 && (
                <div className="mes-section timeline-section">
                  <div className="timeline-section-header">
                    <h3>Timeline — faze za {selectedDate}</h3>
                    {renderViewToggle()}
                  </div>
                  {renderPhaseDayStrip(sameDayBatches)}
                  {renderTimelineView(false, sameDayBatches, { editableStatus: true })}
                </div>
              )}

              {generatedBatches.length > 0 && (
                <OperationalReorderPanel
                  batches={sameDayBatches}
                  onApply={handleOperationalReorder}
                  disabled={loading}
                  formatTime={formatTime}
                />
              )}
            </>
          )}
          </div>

          {/* Product Sidebar - Right Side (only on step 1) */}
          {canEditPlan && wizardStep === 1 && sidebarOpen && (
            <div className="mes-section" style={{
              width: "320px",
              flexShrink: 0,
              maxHeight: "calc(100vh - 200px)",
              overflowY: "auto",
              position: "sticky",
              top: "20px",
              alignSelf: "flex-start",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                <h3 style={{ margin: 0 }}>Proizvodi</h3>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="btn-icon"
                  title="Zatvori sidebar"
                  style={{
                    background: "transparent",
                    border: "1px solid #4a5f7f",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    color: "#95a5a6",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  ›
                </button>
              </div>

              <input
                type="text"
                placeholder="Pretrazi proizvode..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="input-classic"
                style={{
                  width: "100%",
                  marginBottom: "15px",
                }}
              />

              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {filteredProducts.map((product) => (
                  <ProductSpecCard
                    key={product.ID}
                    product={product}
                    onClick={handleAddProductFromSidebar}
                    compact={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sidebar Toggle Button (when closed) */}
          {canEditPlan && wizardStep === 1 && !sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="mes-sidebar-toggle"
              style={{
                position: "fixed",
                right: "20px",
                top: "150px",
                background: "#2c3e50",
                border: "2px solid #4a5f7f",
                borderRadius: "8px 0 0 8px",
                padding: "12px 8px",
                color: "#ecf0f1",
                cursor: "pointer",
                zIndex: 100,
                fontSize: "14px",
                fontWeight: "500",
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                boxShadow: "-2px 2px 8px rgba(0,0,0,0.2)",
              }}
              title="Otvori sidebar proizvoda"
            >
              ‹ Proizvodi
            </button>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      {confirmModalOpen && (
        <div className="mes-modal-overlay" onClick={() => setConfirmModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">✓</div>
                <div className="modal-title-text">
                  <h3>POTVRDI TIMELINE</h3>
                  <p className="modal-subtitle">Potvrda plana proizvodnje</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setConfirmModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: "1rem" }}>Zelite li dodijeliti lotove sirovinama prije potvrde?</p>
              <div className="form-group">
                <label>Napomene (opcionalno)</label>
                <textarea
                  placeholder="Unesite dodatne napomene..."
                  value={confirmNotes}
                  onChange={(e) => setConfirmNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setConfirmModalOpen(false)}>
                Odustani
              </button>
              <button className="btn-secondary" onClick={handleSkipLotAssignment} disabled={loading}>
                {loading ? "Potvrdjivanje..." : "Potvrdi bez lotova"}
              </button>
              <button className="btn-submit" onClick={handleOpenLotAssignment} disabled={loading}>
                Potvrdi s lotovima
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lot Assignment Modal */}
      {lotAssignmentModalOpen && timeline && timeline.TimelineBatch && (
        <div className="mes-modal-overlay" onClick={() => setLotAssignmentModalOpen(false)}>
          <div className="mes-modal lot-assignment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">📦</div>
                <div className="modal-title-text">
                  <h3>DODIJELI LOTOVE</h3>
                  <p className="modal-subtitle">Assign ingredient lots to batches</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setLotAssignmentModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <LotAssignment
                batches={timeline.TimelineBatch}
                timelineId={timeline.ID}
                productionDate={selectedDate}
                onComplete={handleLotAssignmentComplete}
                onCancel={() => setLotAssignmentModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />

    </div>
  );
};

export default Planning;
